import type { NearV1Config, NearV1Crank, NearV1CrankAction, NearV1Progress } from '../../../../packages/sdk/src/near';
import { canonicalRaw } from './near-lifecycle';

const methods: Record<string, readonly string[]> = {
  factory: ['continue_creation', 'continue_registration', 'continue_growth_inherit', 'reconcile_creation'],
  launch: ['advance_pool_seed', 'verify_pool_activation', 'reconcile_pool'],
  executor: ['reconcile_dispatch', 'observe_verified_mint', 'withdraw_return', 'retry_return_evidence'],
  pool: ['verify_swap_recipient_storage', 'advance_swap_tax', 'reconcile_swap_tax', 'advance_swap_output',
    'reconcile_swap', 'retry_swap_output', 'cancel_failed_swap', 'reconcile_token_claim', 'retry_token_claim'],
};

export function sameCrankAction(a: NearV1CrankAction, b: NearV1CrankAction): boolean {
  const fields = ['receiverId', 'receiverRole', 'receiverCodeHash', 'methodName', 'gas', 'depositYocto', 'requiredSigner', 'reviewedStage'] as const;
  return fields.every(key => a[key] === b[key]) && JSON.stringify(a.args) === JSON.stringify(b.args);
}

export function assertNearCrank(config: NearV1Config, crank: NearV1Crank, accountId: string, parentOperationId: string, progress?: NearV1Progress): void {
  const action = crank.action;
  if (!config.network || crank.network !== config.network || crank.signerId !== accountId ||
      crank.parentOperationId !== parentOperationId || crank.state !== 'prepared' || crank.expiresAt <= Date.now()) {
    throw new Error('The continuation changed account, network, operation or expiry.');
  }
  if (action.requiredSigner !== 'wallet' || !methods[action.receiverRole]?.includes(action.methodName) ||
      !action.finalBlockHash || !config.contracts.some(pin => pin.network === config.network &&
        pin.accountId === action.receiverId && pin.role === action.receiverRole &&
        pin.codeHash === action.receiverCodeHash && pin.scope === 'authenticated_review' && !!pin.verifiedAtFinalBlock)) {
    throw new Error('The continuation has no matching approved wallet method and contract pin.');
  }
  if (canonicalRaw(action.gas) === 0n || canonicalRaw(action.gas) > 300_000_000_000_000n ||
      canonicalRaw(action.depositYocto) !== 0n || canonicalRaw(crank.maximumGasReserveYocto) === 0n ||
      canonicalRaw(crank.maximumTotalDebitYocto) !== canonicalRaw(crank.maximumGasReserveYocto)) {
    throw new Error('The continuation gas or native debit is invalid.');
  }
  if (progress && (progress.parentOperationId !== parentOperationId || progress.stage !== action.reviewedStage ||
      (progress.nextCrank && !sameCrankAction(progress.nextCrank, action)))) {
    throw new Error('The continuation differs from the current operation progress.');
  }
  if (action.receiverRole === 'pool') {
    const swap = ['reconcile_swap','retry_swap_output','cancel_failed_swap'].includes(action.methodName);
    const claim = ['reconcile_token_claim','retry_token_claim'].includes(action.methodName);
    const taxGas: Record<string,string> = {
      verify_swap_recipient_storage:'90000000000000', advance_swap_tax:'220000000000000',
      reconcile_swap_tax:'110000000000000', advance_swap_output:'90000000000000',
    };
    const tax = action.methodName in taxGas;
    const stage = action.reviewedStage;
    const taxStage = action.methodName === 'verify_swap_recipient_storage' ? stage.startsWith('PendingTax:AwaitingRecipient:') :
      action.methodName === 'advance_swap_tax' ? stage.startsWith('PendingTax:AwaitingTax:') :
      action.methodName === 'reconcile_swap_tax' ? stage.startsWith('PendingTax:TaxPending:') || stage.startsWith('PendingTax:TaxUnknown:') :
      action.methodName === 'advance_swap_output' && stage.startsWith('PendingTax:TaxFunded:');
    const outputStage = stage.startsWith('Pending:') ||
      action.methodName !== 'cancel_failed_swap' && stage.startsWith('PendingTax:TaxFunded:');
    const stagePrefix = tax ? 'PendingTax:' : swap ? 'Pending:' : 'PendingClaim:';
    if ((!swap && !claim && !tax) || action.gas !== (tax ? taxGas[action.methodName] : '180000000000000') ||
        !(tax ? taxStage : swap ? outputStage : stage.startsWith('PendingClaim:')) ||
        stage.length <= stagePrefix.length || stage.endsWith(':') ||
        (swap || tax ? Object.keys(action.args).length !== 0 :
          Object.keys(action.args).length !== 1 || typeof action.args.creator_token !== 'boolean') ||
        (progress && (swap || tax ? !['swap_creator_to_receipt','swap_receipt_to_creator'].includes(progress.action) ||
          !!progress.businessEffect?.poolEffectId && !stage.endsWith(`:${progress.businessEffect.poolEffectId}`) :
          progress.action !== (action.args.creator_token ? 'claim_pool_creator' : 'claim_pool_receipt')))) {
      throw new Error('The pool continuation method, arguments or effect stage changed.');
    }
  }
}
